/**
 * Stylin' Strings: final sale agreement (snippets/ss-final-sale.liquid).
 *
 * Until the box is ticked, clicks on Add to cart (and the sticky bar's button) are stopped before
 * Horizon or Zepto see them, and the box is highlighted with the reason instead. The buttons keep
 * taking clicks (rather than being `disabled`) so they can explain themselves, and because Horizon
 * re-sets `disabled` on every variant change.
 */

/** Product IDs agreed to on this page, so a re-render on variant change keeps the box ticked. */
const agreed = new Set();

class SsFinalSale extends HTMLElement {
  connectedCallback() {
    this.checkbox = /** @type {HTMLInputElement | null} */ (this.querySelector('.ss-final-sale__checkbox'));
    if (!this.checkbox) return;

    if (agreed.has(this.dataset.productId)) this.checkbox.checked = true;
    this.checkbox.addEventListener('change', this.#update);
    this.#update();
  }

  disconnectedCallback() {
    this.checkbox?.removeEventListener('change', this.#update);
  }

  get agreed() {
    return Boolean(this.checkbox?.checked);
  }

  #update = () => {
    const id = this.dataset.productId || '';
    if (this.agreed) agreed.add(id);
    else agreed.delete(id);

    this.toggleAttribute('data-agreed', this.agreed);
    this.checkbox?.setCustomValidity(this.agreed ? '' : this.querySelector('.ss-final-sale__hint')?.textContent?.trim() || '');
  };

  /** Point the customer at the box after a blocked add to cart. */
  remind() {
    this.setAttribute('data-reminded', '');
    this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    this.checkbox?.focus({ preventScroll: true });
  }
}

if (!customElements.get('ss-final-sale')) {
  customElements.define('ss-final-sale', SsFinalSale);

  /**
   * The agreement box that applies to an add-to-cart control, if it hasn't been ticked yet.
   * @param {Element} control
   * @returns {SsFinalSale | null}
   */
  const pendingFor = (control) => {
    // The sticky bar sits in the same section as the product form it adds from
    const scope = control.closest('product-form-component') || control.closest('.shopify-section');
    const box = /** @type {SsFinalSale | null} */ (scope?.querySelector('ss-final-sale') ?? null);
    return box && !box.agreed ? box : null;
  };

  /** @param {Event} event @param {SsFinalSale} box */
  const block = (event, box) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    box.remind();
  };

  window.addEventListener(
    'click',
    (event) => {
      const control = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null)?.closest(
        '[ref="addToCartButton"], .sticky-add-to-cart__button, .accelerated-checkout-block'
      );
      if (!control) return;
      const box = pendingFor(control);
      if (box) block(event, box);
    },
    true
  );

  window.addEventListener(
    'submit',
    (event) => {
      const form = event.target instanceof HTMLFormElement ? event.target : null;
      const box = /** @type {SsFinalSale | null} */ (form?.querySelector('ss-final-sale') ?? null);
      if (box && !box.agreed) block(event, box);
    },
    true
  );
}
